import { useMemo } from 'react'
import { ApolloClient, ApolloError, ApolloQueryResult, NormalizedCacheObject, useQuery } from '@apollo/client'
import { isAfter, parseISO } from 'date-fns'
import { Maybe } from 'graphql/jsutils/Maybe'
import { projectTypes } from '@/constants/projectTypes'
import { slugs } from '@/constants/slugs'
import { ContentfulClientOptions, getContentfulClient, getWebClient } from '@/services/ApolloClient'
import { findEpisodeGuidBySlug } from '@/services/ProjectsService/FriendlyEpisodeSlugHelper'
import {
  FETCH_SERIES_EPISODE_STILLS,
  GET_ALL_SITEMAP_WATCHABLES,
  GET_AVAILABLE_COLLECTIBLE_OFFERS_BY_PROJECT,
  GET_AVAILABLE_MOMENT,
  GET_CONTENT_EPISODE,
  GET_CONTENT_VIDEO,
  GET_DISCOVER_SNEAK_PEEKS,
  GET_EPISODE_BY_GUID_QUERY,
  GET_EPISODE_FROM_MOMENT,
  GET_EPISODE_FROM_MOMENT_WITH_USER,
  GET_FRAME_BY_ID,
  GET_GUILD_ACCESS_PROJECTS,
  GET_LIGHT_PROJECT_FOR_SHARE,
  GET_MOMENT_ID_FROM_STORY,
  GET_MOVIE_AND_SPECIAL_WATCHABLES_QUERY,
  GET_NEXT_CONTENT,
  GET_PREVIEWABLE_CONTENT,
  GET_PROJECT_GUILD_SCORE,
  GET_PROJECT_HOVER_DATA,
  GET_PROJECT_HOVER_DATA_BY_SLUG,
  GET_PROJECT_PHASE,
  GET_PROJECT_SEO_METADATA,
  GET_PROJECT_VIDEOS_QUERY,
  GET_RESUME_WATCHING_FOR_PROJECT,
  GET_START_WATCHING_QUERY,
  GET_THEATRICAL_RELEASE_BY_SLUG,
  GET_WATCHABLE_AVAILABILITY,
  GET_WATCHABLE_EPISODE,
  getAllProjectsQuery,
  getContentfulProjectConfig,
  getCurrentLivestreamQuery,
  getProjectAndEpisodeQuery,
  getProjectAndEpisodeQueryForDeepLink,
  getProjectAndFaqQuery,
  getProjectDetailsQuery,
  getProjectMetaQuery,
  getProjectQuery,
  getProjectSitemapsQuery,
} from '@/services/ProjectsService/queries'
import {
  ContentEpisode,
  ContentVideo,
  CurrentLivestream,
  Episode,
  EpisodeWithStills,
  GetEpisodeResponse,
  GetProjectParams,
  GetProjectVideosParams,
  GetProjectVideosResponse,
  MediaQuery,
  NextContentEpisodeSpecialOrMovie,
  PreviewableEpisodeOrSpecial,
  Project,
  ProjectLinkMeta,
  ProjectMeta,
  ProjectSeoMetadata,
  ProjectsWithSlugs,
  ProjectType,
  ResumeWatchingData,
  Season,
  SitemapWatchableNode,
  UnifiedProjectHoverContent,
  UseContentAvailabilityResponse,
  VALID_PROJECT_TYPES,
  VideoMetaData,
  WatchableNode,
} from '@/services/ProjectsService/types'
import { GetGuildAccessProjectsQuery, TheatricalRegion } from '@/types/codegen-contentful'
import {
  AvailableCollectibleOffersInput,
  ContentAvailabilityFragment,
  ContentSneakPeek,
  FetchProjectGuildScoreQuery,
  FetchProjectSeoMetadataQuery,
  GetAvailableCollectibleOffersByProjectQuery,
  GetAvailableMomentQuery,
  GetContentEpisodeQuery,
  GetContentVideoQuery,
  GetCurrentLivestreamQuery,
  GetDiscoverSneakPeeksQuery,
  GetEpisodeFromMomentQuery,
  GetEpisodeFromMomentWithUserQuery,
  GetFrameByIdQuery,
  GetLightProjectForShareQuery,
  GetMovieAndSpecialsWatchablesQuery,
  GetNextContentQuery,
  GetNextContentQueryVariables,
  GetPreviewableContentQuery,
  GetProjectDetailsQuery,
  GetProjectMetaQuery,
  GetProjectPhaseQuery,
  GetProjectPhaseQueryVariables,
  GetSeriesEpisodeStillsBySlugQuery,
  GetStartWatchingQuery,
  GetTheatricalReleaseBySlugQuery,
  GetWatchableAvailabilityQuery,
  GetWatchableAvailabilityQueryVariables,
  GetWatchableEpisodeQuery,
  GetWatchableEpisodeQueryVariables,
  GetWatchableHoverContentBySlugQuery,
  GetWatchableHoverContentQuery,
  PhaseSlug,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { getTheatricalReleaseBySlug } from '../CmsService/TheatricReleases'
import { getLightFranchiseBySlug, LightFranchise } from '../FranchiseService'
import { FlowPhaseForStatusCheck, isSellingTickets } from '../PhaseManagerService'
import { getTheatricalRelease, TheatricalRelease } from '../Theatrical/queries'

export function isValidProjectType(projectType: Maybe<string>): projectType is ProjectType {
  return !!projectType && VALID_PROJECT_TYPES.includes(projectType as ProjectType)
}

export const getAllProjects = async (client: ApolloClient<object>): Promise<ApolloQueryResult<ProjectsWithSlugs>> => {
  return await client.query({
    query: getAllProjectsQuery,
    errorPolicy: 'all',
  })
}

export const getProjectPhase = async (client: ApolloClient<object>, projectSlug: string): Promise<PhaseSlug | null> => {
  try {
    const { data } = await client.query<GetProjectPhaseQuery, GetProjectPhaseQueryVariables>({
      query: GET_PROJECT_PHASE,
      variables: { projectSlug },
      errorPolicy: 'all',
    })

    return data?.projectBySlug?.primaryFlowPhases?.find((phase) => phase?.status === 'current')?.phaseSlugEnum ?? null
  } catch (err) {
    logger().error('getProjectPhase query failed', err)
    return null
  }
}

export const getProjectMeta = async (
  { slug }: GetProjectParams,
  client: ApolloClient<object>,
  opts: ContentfulClientOptions,
): Promise<ProjectMeta> => {
  const { data } = await client.query({
    query: getProjectMetaQuery,
    variables: {
      slug,
    },
    errorPolicy: 'all',
  })

  const contentfulClient = getContentfulClient({
    locale: opts.locale ?? 'en',
    preview: opts.preview,
  })

  let contentfulProjectData

  try {
    const { data: contentfulData } = await contentfulClient.query({
      query: getContentfulProjectConfig,
      variables: { projectSlug: slug },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    })

    contentfulProjectData = contentfulData?.projectCollection?.items?.[0]
  } catch (e) {
    logger().error('Failure to get project data from Contentful', { projectSlug: slug }, e)
  }

  return {
    ...data.project,
    ...contentfulProjectData,
  }
}

export const getProjectAbout = async (
  { slug }: { slug: Maybe<string> },
  client: ApolloClient<object>,
  opts?: ContentfulClientOptions,
): Promise<ProjectMeta | null> => {
  if (!slug) return null

  let projectMeta: GetProjectMetaQuery | null = null
  try {
    const { data } = await client.query<GetProjectMetaQuery>({
      query: getProjectMetaQuery,
      variables: {
        slug,
      },
      errorPolicy: 'all',
    })
    projectMeta = data
  } catch (e) {
    logger().error(`Failed to fetch project meta from Hydra with slug ${slug}`, { slug }, e)
    return null
  }

  const contentfulClient = getContentfulClient({
    locale: opts?.locale ?? 'en',
    preview: opts?.preview,
  })

  let contentfulProjectData
  let contentfulFaqData
  let contentfulDownloadableData
  let rottenTomatoes: unknown = null
  let audience: unknown = null
  let cinema: unknown = null
  let imdb: unknown = null

  try {
    const { data: contentfulData } = await contentfulClient.query({
      query: getProjectAndFaqQuery,
      variables: { projectSlug: slug, locale: opts?.locale ?? 'en', preview: opts?.preview },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    })

    contentfulProjectData = contentfulData?.projectCollection?.items?.[0]
    contentfulFaqData = contentfulData?.faqCollection?.items
    contentfulDownloadableData = contentfulData?.downloadableCollection?.items

    rottenTomatoes = contentfulProjectData?.rottenTomatoes?.items?.[0] ?? null
    audience = contentfulProjectData?.audience?.items?.[0] ?? null
    cinema = contentfulProjectData?.cinema?.items?.[0] ?? null
    imdb = contentfulProjectData?.IMDb?.items?.[0] ?? null
  } catch (e) {
    logger().error(`Failed to get project metadata from Contentful with slug ${slug}`, { slug }, e)
  }

  return {
    ...projectMeta.project,
    ...contentfulProjectData,
    faqs: contentfulFaqData ?? [],
    downloadables: contentfulDownloadableData ?? [],
    rottenTomatoes: rottenTomatoes ?? null,
    audience: audience ?? null,
    cinema: cinema ?? null,
    imdb: imdb ?? null,
  }
}

interface EpisodeDetailsForDeepLink {
  project: {
    projectType?: ProjectType
    discoveryPosterLandscapeCloudinaryPath?: string
    name?: string
    slug?: string
    seasons?: {
      episodes?: {
        guid?: string
        projectSlug?: string
      }[]
    }[]
  }
  episode: {
    episodeNumber?: number
    seasonNumber?: number
    description?: string
    subtitle?: string
    projectSlug?: string
  }
  backgroundImageCloudinaryPath: string | null
}

export const getEpisodeDetailsForDeepLink = async (
  { guid, projectSlug }: MediaQuery,
  client: ApolloClient<object>,
): Promise<EpisodeDetailsForDeepLink> => {
  let result
  try {
    const { data } = await client.query({
      query: getProjectAndEpisodeQueryForDeepLink,
      variables: {
        guid,
        projectSlug,
      },
      errorPolicy: 'all',
    })

    result = data
  } catch (e) {
    logger().warn('Episode could not be looked up by guid', { guid, projectSlug }, e)
  }

  if (!result?.episode) {
    const updatedGuid = await findEpisodeGuidBySlug(client, guid, projectSlug)

    if (updatedGuid) {
      const { data } = await client.query({
        query: getProjectAndEpisodeQueryForDeepLink,
        variables: {
          guid: updatedGuid,
          projectSlug,
        },
        errorPolicy: 'all',
      })
      result = data
    }
  }

  if (projectSlug !== result?.episode?.projectSlug) {
    throw Error('Episode does not belong to Project')
  }

  return result
}

export const getEpisode = async (
  { guid, projectSlug, skipsEnabled = false }: MediaQuery,
  client: ApolloClient<object>,
): Promise<GetEpisodeResponse | null> => {
  // I think bad urls are getting generated upstream. The immedidate upstream functions are checking for falsey values,
  // but "undefined" is still coming through. It's likely that somewhere code is generating degenerate urls.
  // We need to protect against degenerate inputs here to prevent errors.
  if (
    typeof guid === 'undefined' ||
    !guid ||
    guid === 'undefined' ||
    typeof projectSlug === 'undefined' ||
    !projectSlug ||
    projectSlug === 'undefined'
  ) {
    logger().warn('ProjectsService.getEpisode: Both guid and projectSlug are required; one or both is invalid.', {
      guid,
      projectSlug,
    })
    return null
  }

  try {
    const { data } = await client.query({
      query: getProjectAndEpisodeQuery,
      variables: { guid, projectSlug, skipsEnabled },
      errorPolicy: 'all',
    })

    if (validateEpisodeResponse(data, projectSlug)) {
      return data
    }
  } catch (err) {
    logger().warn(`getProjectAndEpisodeQuery failed`, { guid, projectSlug }, err)
    if (err instanceof Error && err.message.includes('does not belong to Project')) return null
  }

  try {
    const updatedGuid = await findEpisodeGuidBySlug(client, guid, projectSlug)
    if (!updatedGuid) {
      logger().warn(`Episode guid could not be looked up by project slug`, { guid, projectSlug })
      return null
    }

    const { data } = await client.query({
      query: getProjectAndEpisodeQuery,
      variables: {
        guid: updatedGuid,
        projectSlug,
      },
      errorPolicy: 'all',
    })

    if (validateEpisodeResponse(data, projectSlug)) {
      return data
    } else {
      return null
    }
  } catch (err) {
    logger().error(`getProjectAndEpisodeQuery failed using guid from projectSlug`, { guid, projectSlug }, err)
    return null
  }
}

function validateEpisodeResponse(data: Maybe<GetEpisodeResponse>, projectSlug: string): data is GetEpisodeResponse {
  const isValidResponse = Boolean(
    data && data.episode && data.episode.projectSlug && data.episode.guid && data.project && data.project.slug,
  )
  if (!isValidResponse) return false

  if (projectSlug !== data?.episode?.projectSlug) {
    throw Error(
      `Episode (${data?.episode?.guid}) does not belong to Project (${projectSlug}) and episode project slug ${data?.episode?.projectSlug} do not match.`,
    )
  }
  return true
}

export const getEpisodeByGuid = async ({ guid }: { guid: string }, client: ApolloClient<object>): Promise<Episode> => {
  let result
  try {
    const { data } = await client.query({
      query: GET_EPISODE_BY_GUID_QUERY,
      variables: {
        guid,
      },
      errorPolicy: 'all',
    })

    result = data
  } catch (e) {
    logger().warn(`Episode could not be looked up by guid=${guid}`, e)
  }

  return result.episode
}

export const getLightProjectForShare = async (
  { slug }: { slug: string },
  client: ApolloClient<object>,
): Promise<GetLightProjectForShareQuery['project']> => {
  try {
    const { data } = await client.query({
      query: GET_LIGHT_PROJECT_FOR_SHARE,
      variables: {
        projectSlug: slug,
      },
      errorPolicy: 'all',
    })

    return data?.project
  } catch (e) {
    logger().warn('Project could not be looked up by slug', { slug }, e)
    return null
  }
}

export const getEpisodeFromMomentWithUser = async (
  { momentId, shareUserId }: { momentId: string; shareUserId: string },
  client: ApolloClient<object>,
): Promise<GetEpisodeFromMomentWithUserQuery> => {
  let result
  try {
    const { data } = await client.query({
      query: GET_EPISODE_FROM_MOMENT_WITH_USER,
      variables: {
        momentId,
        shareUserId,
      },
      errorPolicy: 'all',
    })

    result = data
  } catch (e) {
    logger().warn('Episode could not be looked up by momentId', { momentId, shareUserId }, e)
  }

  if (!result?.moment?.episode) {
    throw Error('Moment does not belong to an Episode')
  }

  if (!result?.moment?.episode?.projectSlug) {
    throw Error('Moments Episode does not belong to a Project')
  }

  return result
}

export type StartWatchingProject = NonNullable<NonNullable<GetStartWatchingQuery>['projects']>[number]

export async function getStartWatchingList(): Promise<StartWatchingProject[]> {
  const client = getWebClient()

  try {
    const response = await client.query<GetStartWatchingQuery>({
      query: GET_START_WATCHING_QUERY,
      errorPolicy: 'all',
    })

    return isDefined(response.data.projects) ? response.data.projects?.filter(isDefined) : []
  } catch (error) {
    logger().error('Error fetching start watching list', { error })
    return []
  }
}

export const getMomentFromStory = async (
  { userStoryId }: { userStoryId: string },
  client: ApolloClient<object>,
): Promise<{ moment: GetEpisodeFromMomentQuery['moment'] | null }> => {
  let result
  try {
    const { data } = await client.query({
      query: GET_MOMENT_ID_FROM_STORY,
      variables: {
        userStoryId,
      },
      errorPolicy: 'all',
    })

    result = data
  } catch (e) {
    logger().warn(`Moment could not be looked up by storyId=${userStoryId}`, e)
  }

  const momentId = result?.userStory?.momentId || null

  let moment

  try {
    const response = await getEpisodeFromMoment({ momentId }, client)
    moment = response.moment
  } catch (e) {
    logger().warn(`Couldn't find related moment for storyId=${userStoryId}`, e)
  }

  return {
    moment,
  }
}

export const getAvailableCollectibleOffersByProject = async (
  { input }: { input: AvailableCollectibleOffersInput },
  client: ApolloClient<object>,
): Promise<GetAvailableCollectibleOffersByProjectQuery['availableCollectibleOffers']> => {
  try {
    const { data } = await client.query<GetAvailableCollectibleOffersByProjectQuery>({
      query: GET_AVAILABLE_COLLECTIBLE_OFFERS_BY_PROJECT,
      variables: {
        input,
      },
      errorPolicy: 'all',
    })

    return data.availableCollectibleOffers?.filter(isDefined) ?? []
  } catch (error) {
    logger().warn('Could not find available collectible offers by project', { input }, error)
    return []
  }
}

export const getAvailableMoment = async (
  { momentId }: { momentId: number },
  client: ApolloClient<object>,
): Promise<{ moment: GetAvailableMomentQuery['moment'] | null }> => {
  let result

  try {
    const { data } = await client.query({
      query: GET_AVAILABLE_MOMENT,
      variables: {
        momentId,
      },
      errorPolicy: 'all',
    })

    result = data
  } catch (e) {
    logger().warn('Could not find moment', { momentId }, e)
  }

  return result
}

export const getEpisodeFromMoment = async (
  { momentId }: { momentId: string },
  client: ApolloClient<object>,
): Promise<{ moment: GetEpisodeFromMomentQuery['moment'] | null }> => {
  let result

  try {
    const { data } = await client.query({
      query: GET_EPISODE_FROM_MOMENT,
      variables: {
        momentId,
      },
      errorPolicy: 'all',
    })

    result = data
  } catch (e) {
    logger().warn('Episode could not be looked up by momentId', { momentId }, e)
  }

  if (!result?.moment?.episode) {
    throw Error('Moment does not belong to an Episode')
  }

  if (!result?.moment?.episode?.projectSlug) {
    throw Error('Moments Episode does not belong to a Project')
  }

  return {
    moment: result.moment,
  }
}

export const getFrameById = async (
  { frameId }: { frameId: string },
  client: ApolloClient<object>,
): Promise<{ frame: GetFrameByIdQuery['frame'] | null }> => {
  let result
  try {
    const { data } = await client.query({
      query: GET_FRAME_BY_ID,
      variables: {
        frameId,
      },
      errorPolicy: 'all',
    })
    result = data
  } catch (e) {
    logger().warn('Frame could not be found', { frameId }, e)
  }

  return {
    frame: result.frame,
  }
}

interface ContentVideoResult {
  projectName?: string
  logoCloudinaryPath: string
  projectSlug?: string
  public?: boolean
  video: ContentVideo
}

export const getContentVideo = async (
  { guid }: { guid: string },
  client: ApolloClient<object>,
): Promise<ContentVideoResult | null> => {
  try {
    const { data } = await client.query<GetContentVideoQuery>({
      query: GET_CONTENT_VIDEO,
      variables: {
        guid,
      },
      errorPolicy: 'all',
    })
    const project = data?.getContent && 'project' in data.getContent ? data.getContent.project : null
    const logo = data?.getContent && 'logo' in data.getContent ? data.getContent.logo : null
    const video = (data?.getContent as ContentVideo) ?? null

    return {
      projectName: project?.name ?? undefined,
      logoCloudinaryPath: logo?.cloudinaryPath ?? '',
      projectSlug: project?.slug ?? undefined,
      public: project?.public ?? undefined,
      video: video as ContentVideo,
    }
  } catch (error) {
    logger().error('getContentVideo query failed', { guid }, error)
    return null
  }
}

interface ContentEpisodeResult {
  projectName?: string
  logoCloudinaryPath: string
  projectSlug?: string
  public?: boolean
  pifEnabled?: boolean
  episode: ContentEpisode
}

export const getContentEpisode = async (
  { guid }: { guid: string },
  client: ApolloClient<object>,
): Promise<ContentEpisodeResult | null> => {
  try {
    const { data } = await client.query<GetContentEpisodeQuery>({
      query: GET_CONTENT_EPISODE,
      variables: {
        guid,
      },
      errorPolicy: 'all',
    })
    const project = data?.getContent && 'project' in data.getContent ? data.getContent.project : null
    const logo = data?.getContent && 'logo' in data.getContent ? data.getContent.logo : null
    const episode = (data?.getContent as ContentEpisode) ?? null

    return {
      projectName: project?.name ?? undefined,
      logoCloudinaryPath: logo?.cloudinaryPath ?? '',
      projectSlug: project?.slug ?? undefined,
      public: project?.public ?? undefined,
      pifEnabled: Boolean(project && 'pifEnabled' in project && project?.pifEnabled) || false,
      episode: episode as ContentEpisode,
    }
  } catch (error) {
    logger().error('getContentEpisode query failed', { guid }, error)
    return null
  }
}

export const getProjectSitemaps = async (client: ApolloClient<object>): Promise<ProjectLinkMeta[]> => {
  const { data } = await client.query({
    query: getProjectSitemapsQuery,
    errorPolicy: 'all',
  })

  return data.projects
}

export const getAllSitemapWatchables = async (client: ApolloClient<object>): Promise<SitemapWatchableNode[]> => {
  try {
    const { data } = await client.query<{
      listWatchables?: { edges?: Array<{ node?: SitemapWatchableNode | null } | null> }
    }>({
      query: GET_ALL_SITEMAP_WATCHABLES,
      errorPolicy: 'all',
    })
    const edges = data.listWatchables?.edges || []
    return edges.map((edge) => edge?.node).filter(isDefined)
  } catch (error) {
    logger().error('getAllSitemapWatchables query failed', { error })
    return []
  }
}

export const getPreferredSeason = (project: Project, resumeWatchingData?: ResumeWatchingData) => {
  const seasons = project.seasons || []

  if (seasons.length === 0) {
    return
  }

  // Use resumeWatching data if provided (only ContentEpisode has season)
  if (
    resumeWatchingData?.content?.__typename === 'ContentEpisode' &&
    resumeWatchingData.content.season?.id &&
    project.slug
  ) {
    const episodeContent = resumeWatchingData.content
    const found = seasons.find(
      (season: Season) => season.id === episodeContent.season?.id && episodeContent.project?.slug === project.slug,
    )
    return found ?? seasons[0]
  } else if (project.slug === slugs.theChosen) {
    return seasons[seasons.length - 1]
  } else {
    return seasons[0]
  }
}

export const getSeasonByNameOrIndex = ({
  seasonNameOrIndex,
  project,
}: {
  seasonNameOrIndex: string
  project: Project
}) => {
  const seasons = project.seasons || []

  if (seasons.length === 0) {
    return
  }

  if (seasonNameOrIndex) {
    const seasonByName = seasons.find((current) => current.name?.includes(seasonNameOrIndex))
    const seasonIndex = parseInt(seasonNameOrIndex) - 1

    if (seasonByName) {
      return seasonByName
    } else if (!isNaN(seasonIndex) && seasons[seasonIndex]) {
      return seasons[seasonIndex]
    } else {
      return seasons[0]
    }
  }
}

export const getProjectDetails = async ({ slug }: { slug: string }, client: ApolloClient<object>) => {
  const { data } = await client.query<GetProjectDetailsQuery>({
    query: getProjectDetailsQuery,
    variables: { slug },
    errorPolicy: 'all',
  })

  return data?.project
}

export const getGuildEarlyAccessProjects = async (opts?: ContentfulClientOptions) => {
  const client = getContentfulClient(opts)
  const { data } = await client.query<GetGuildAccessProjectsQuery>({
    query: GET_GUILD_ACCESS_PROJECTS,
  })

  return data.projectCollection?.items
}

export const getWatchProjectEarlyAccess = async (
  projectSlug: string,
  projectMeta: ProjectMeta,
  client: ApolloClient<object>,
) => {
  let result

  try {
    const { data } = await client.query({
      query: getProjectQuery,
      variables: {
        slug: projectSlug,
      },
      errorPolicy: 'all',
    })
    result = data.project
  } catch (e) {
    logger().warn('Could not fetch watch project early access', { projectSlug }, e)
  }

  return result
}

export const getProjectGuildScore = async (
  projectSlug: string,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<{
  guildScore: number | null
}> => {
  try {
    const { data } = await client.query<FetchProjectGuildScoreQuery>({
      query: GET_PROJECT_GUILD_SCORE,
      variables: { projectSlug },
      errorPolicy: 'all',
    })

    return {
      guildScore: data.project?.highestScore ?? null,
    }
  } catch (error) {
    logger().error('Fetching Guild Score failed', {
      error,
      projectSlug,
      context: 'getProjectGuildScore',
    })
    return {
      guildScore: null,
    }
  }
}

export const getProjectSeoMetadata = async (
  projectSlug: string,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<ProjectSeoMetadata> => {
  try {
    const { data } = await client.query<FetchProjectSeoMetadataQuery>({
      query: GET_PROJECT_SEO_METADATA,
      variables: { projectSlug },
      errorPolicy: 'all',
    })

    return {
      name: data.project?.title?.name ?? null,
      description: data.project?.title?.mediumDescription ?? null,
      imagePath: data.project?.title?.titleArt?.cloudinaryPath ?? null,
    }
  } catch (error) {
    logger().error('Fetching Project SEO Metadata failed', {
      error,
      projectSlug,
      context: 'getProjectSeoMetadata',
    })
    return {
      name: null,
      description: null,
      imagePath: null,
    }
  }
}

export const getMovieAndSpecialsWatchables = async (
  slug: string,
  client: ApolloClient<object>,
): Promise<{
  watchables: WatchableNode[] | undefined
}> => {
  try {
    const { data } = await client.query<GetMovieAndSpecialsWatchablesQuery>({
      query: GET_MOVIE_AND_SPECIAL_WATCHABLES_QUERY,
      variables: { slug },
      errorPolicy: 'all',
    })
    if (!data?.listWatchables?.edges) return { watchables: [] }

    const nodes = data.listWatchables.edges.map((edge) => edge?.node).filter(isDefined)
    // Move ContentBonus to the end, preserving relative order
    const nonBonus = nodes.filter((n) => n.__typename !== 'ContentBonus')
    const bonus = nodes.filter((n) => n.__typename === 'ContentBonus')
    const sorted = [...nonBonus, ...bonus]

    return { watchables: sorted }
  } catch (error) {
    logger().error(`Error fetching watchables for SLUG: ${slug}`, error)
    return { watchables: [] }
  }
}

export const getCurrentLivestream = async (
  projectId: string,
  client: ApolloClient<object>,
): Promise<CurrentLivestream | null> => {
  try {
    if (!projectId) {
      throw new Error('Falsey value provided for required parameter: projectId')
    }
    const { data } = await client.query<GetCurrentLivestreamQuery>({
      query: getCurrentLivestreamQuery,
      variables: { projectId },
      errorPolicy: 'all',
    })
    return data?.currentLivestream ?? null
  } catch (error) {
    logger().error(`Error fetching current livestream for project ID: ${projectId}`, error)
    return null
  }
}

export const getWatchableEpisodeByGuid = async (
  guid: string,
  client: ApolloClient<object>,
): Promise<{
  watchableReasons: string[] | undefined
}> => {
  try {
    const { data } = await client.query<GetWatchableEpisodeQuery>({
      query: GET_WATCHABLE_EPISODE,
      variables: { guid },
      errorPolicy: 'all',
    })
    if (data?.getContent?.__typename === 'ContentEpisode') {
      return {
        watchableReasons: data.getContent.watchableReasons?.filter(isDefined).map((reason) => reason.__typename),
      }
    }
    return { watchableReasons: undefined }
  } catch (error) {
    logger().error(`Error fetching watchable episode by GUID: ${guid}`, error)
    return { watchableReasons: undefined }
  }
}

export const getPreviewableContentByGuid = async (
  guid: string,
  client: ApolloClient<object>,
): Promise<{
  previewableContent: PreviewableEpisodeOrSpecial | null
  loading: boolean
  error: ApolloError | undefined
}> => {
  try {
    const { data, loading, error } = await client.query<GetPreviewableContentQuery>({
      query: GET_PREVIEWABLE_CONTENT,
      variables: { guid },
      errorPolicy: 'all',
    })

    const content = data?.getContent
    if (!content) {
      logger().error(`No content found for guid: ${guid}`)
      // If no content is found, return null for previewableContent
      return {
        previewableContent: null,
        loading,
        error: undefined,
      }
    }

    const episodeOrSpecial =
      content?.__typename === 'ContentEpisode' || content?.__typename === 'ContentSpecial' ? content : null

    return {
      previewableContent: episodeOrSpecial,
      loading,
      error,
    }
  } catch (err) {
    logger().error(`Error fetching previewable content by GUID: ${guid}`, err)
    return {
      previewableContent: null,
      loading: false,
      error: err instanceof ApolloError ? err : undefined,
    }
  }
}

export const useGetWatchableEpisodeByGuid = (
  guid: string,
  isLoggedIn: boolean,
  client: ApolloClient<NormalizedCacheObject> | undefined,
) => {
  const { data, loading, error } = useQuery<GetWatchableEpisodeQuery, GetWatchableEpisodeQueryVariables>(
    GET_WATCHABLE_EPISODE,
    {
      client,
      variables: { guid: guid },
      skip: !isLoggedIn,
      errorPolicy: 'all',
    },
  )
  const episode = data?.getContent?.__typename === 'ContentEpisode' ? data.getContent : null
  return {
    watchableEpisode: episode,
    loading,
    error,
  }
}

export const useGetWatchableAvailabilityByGuid = (
  guid: string,
  isLoggedIn: boolean,
  client: ApolloClient<NormalizedCacheObject> | undefined,
) => {
  const { data, loading } = useQuery<GetWatchableAvailabilityQuery, GetWatchableAvailabilityQueryVariables>(
    GET_WATCHABLE_AVAILABILITY,
    {
      client,
      variables: { guid: guid },
      skip: !isLoggedIn,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  )
  const content =
    data?.getContent?.__typename === 'ContentEpisode' ||
    data?.getContent?.__typename === 'ContentMovie' ||
    data?.getContent?.__typename === 'ContentSpecial'
      ? data.getContent
      : null
  return {
    watchableContent: content,
    loading,
  }
}

export const getIsGeoblocked = (projectData: Project): boolean => {
  const hasGeoblockedEpisodes = projectData?.seasons?.some((season) =>
    season.episodes?.some((episode) => episode?.unavailableReason?.includes('GEOBLOCKED')),
  )

  const isTitleRegionBlocked = projectData?.title?.watchableAvailabilityStatus === 'REGION_BLOCKED'

  return hasGeoblockedEpisodes || isTitleRegionBlocked
}

export async function fetchTheatricalReleaseData({
  projectType,
  primaryFlowPhases,
  theatricalSlug,
  shouldIncludePreview,
  locale,
}: {
  projectType: string
  primaryFlowPhases: FlowPhaseForStatusCheck[]
  theatricalSlug: string
  shouldIncludePreview: boolean
  locale: string
}): Promise<{
  regionCollection: TheatricalRegion[]
  supportedTheatricalRegions: TheatricalRegion[]
} | null> {
  const hasTicketsAvailable = isSellingTickets(primaryFlowPhases)

  if (projectType === projectTypes.movie && hasTicketsAvailable) {
    const release = await getTheatricalReleaseBySlug({
      theatricalSlug,
      opts: { preview: shouldIncludePreview, locale },
      page: 'ticket',
    })
    return release ?? { regionCollection: [], supportedTheatricalRegions: [] }
  }

  return null
}

export async function fetchTheatricalReleaseMono({
  projectType,
  primaryFlowPhases,
  theatricalSlug,
  client,
}: {
  projectType: string
  primaryFlowPhases: FlowPhaseForStatusCheck[]
  theatricalSlug: string
  client: ApolloClient<NormalizedCacheObject>
}): Promise<TheatricalRelease | null> {
  const hasTicketsAvailable = isSellingTickets(primaryFlowPhases)

  if (projectType === projectTypes.movie && hasTicketsAvailable) {
    const release = await getTheatricalRelease({
      theatricalSlug,
      client,
    })
    return release ?? null
  }

  return null
}

export async function fetchFranchiseData(franchiseSlug?: string): Promise<LightFranchise | null> {
  if (!franchiseSlug) {
    return null
  }

  try {
    return await getLightFranchiseBySlug(franchiseSlug)
  } catch (error) {
    logger().error(`Failed to fetch franchise with slug "${franchiseSlug}"`, { franchiseSlug, error })
    return null
  }
}

const transformSlugDataToUnified = (
  slugData: GetWatchableHoverContentBySlugQuery['project'],
): UnifiedProjectHoverContent | null => {
  if (!slugData?.title) return null

  const title = slugData.title
  const isValidContent =
    title?.__typename === 'ContentSpecial' ||
    title?.__typename === 'ContentMovie' ||
    title?.__typename === 'ContentSeries'

  if (!isValidContent) return null

  // Combine title data with project data
  return {
    ...title,
    project: {
      id: slugData.id,
      slug: slugData.slug,
      highestScore: slugData.highestScore,
      primaryFlowPhases: slugData.primaryFlowPhases?.map((phase) => ({
        phaseSlugEnum: phase?.phaseSlugEnum,
        status: phase?.status,
        releaseWindows: phase?.releaseWindows?.map((window) => ({
          start: window?.start,
        })),
      })),
      logoCloudinaryPath: 'logoCloudinaryPath' in slugData && slugData.logoCloudinaryPath,
    },
  } as UnifiedProjectHoverContent
}

// Helper function to transform GUID-based data to unified format
const transformGuidDataToUnified = (
  guidData: GetWatchableHoverContentQuery['getContent'],
): UnifiedProjectHoverContent | null => {
  if (!guidData) return null

  const isValidContent =
    guidData?.__typename === 'ContentEpisode' ||
    guidData?.__typename === 'ContentSpecial' ||
    guidData?.__typename === 'ContentMovie' ||
    guidData?.__typename === 'ContentSeries'

  if (!isValidContent) return null

  return guidData as UnifiedProjectHoverContent
}

export const getProjectHoverContentByGuid = async (
  guid: string,
  client: ApolloClient<object>,
  projectSlug?: string | null,
): Promise<{
  hoverDataContent: UnifiedProjectHoverContent | null
  loading: boolean
  error: ApolloError | undefined
}> => {
  try {
    const { data, loading, error } = await client.query<GetWatchableHoverContentQuery>({
      query: GET_PROJECT_HOVER_DATA,
      variables: { guid },
      errorPolicy: 'all',
    })

    const content = data?.getContent
    if (!content && projectSlug) {
      logger().info(`No hover content found for guid: ${guid}, trying with projectSlug: ${projectSlug}`)

      try {
        const {
          data: slugData,
          loading: slugLoading,
          error: slugError,
        } = await client.query<GetWatchableHoverContentBySlugQuery>({
          query: GET_PROJECT_HOVER_DATA_BY_SLUG,
          variables: { slug: projectSlug },
          errorPolicy: 'all',
        })

        const unifiedContent = transformSlugDataToUnified(slugData?.project)
        if (!unifiedContent) {
          logger().error(`No hover content found for projectSlug: ${projectSlug}`)
          return {
            hoverDataContent: null,
            loading: slugLoading,
            error: slugError,
          }
        }

        return {
          hoverDataContent: unifiedContent,
          loading: slugLoading,
          error: slugError,
        }
      } catch (slugErr) {
        logger().error(`Error fetching hover content by slug: ${projectSlug}`, slugErr)
        return {
          hoverDataContent: null,
          loading: false,
          error: slugErr instanceof ApolloError ? slugErr : undefined,
        }
      }
    }

    if (!content) {
      logger().error(`No hover content found for guid: ${guid}`)
      return {
        hoverDataContent: null,
        loading,
        error: undefined,
      }
    }

    const unifiedContent = transformGuidDataToUnified(content)
    if (!unifiedContent) {
      logger().error(`Invalid content type for guid: ${guid}`)
      return {
        hoverDataContent: null,
        loading,
        error: undefined,
      }
    }

    return {
      hoverDataContent: unifiedContent,
      loading,
      error,
    }
  } catch (err) {
    logger().error(`Error fetching hover content by GUID: ${guid}`, err)
    return {
      hoverDataContent: null,
      loading: false,
      error: err instanceof ApolloError ? err : undefined,
    }
  }
}

export async function getAllSneakPeeks(client: ApolloClient<NormalizedCacheObject>): Promise<ContentSneakPeek[]> {
  try {
    const { data } = await client.query<GetDiscoverSneakPeeksQuery>({
      query: GET_DISCOVER_SNEAK_PEEKS,
      errorPolicy: 'all',
    })

    if (!data?.listWatchables?.edges) return []

    return data.listWatchables.edges
      .map((edge) => {
        const node = edge?.node

        if (node && node.__typename === 'ContentSneakPeek') {
          return node as ContentSneakPeek
        }
        return undefined
      })
      .filter(isDefined)
      .sort((a, b) => {
        const aDate = a.project?.releaseDate ? new Date(a.project.releaseDate) : new Date(0)
        const bDate = b.project?.releaseDate ? new Date(b.project.releaseDate) : new Date(0)
        return bDate.getTime() - aDate.getTime()
      })
  } catch (error) {
    logger().error('Error fetching sneak peeks', { error })
    return []
  }
}

export async function fetchSeriesEpisodeStills(
  client: ApolloClient<NormalizedCacheObject>,
  projectSlug: string,
): Promise<EpisodeWithStills[]> {
  try {
    const { data } = await client.query<GetSeriesEpisodeStillsBySlugQuery>({
      query: FETCH_SERIES_EPISODE_STILLS,
      variables: { slug: projectSlug },
      errorPolicy: 'all',
    })

    const title = data?.project?.title
    if (title?.__typename !== 'ContentSeries') return []

    const episodesWithStills: EpisodeWithStills[] =
      title.seasons?.edges?.filter(isDefined).flatMap((seasonEdge) => {
        const seasonNode = seasonEdge?.node
        const episodeEdges = seasonNode?.episodes?.edges?.filter(isDefined) ?? []
        const nodes = episodeEdges.map((ep) => ep?.node).filter(isDefined) as EpisodeWithStills[]
        return nodes
      }) ?? []

    return episodesWithStills
  } catch (error) {
    logger().error('Error fetching series episode stills', error)
    return []
  }
}

export const getProjectVideos = async ({
  projectSlug,
  page,
  client,
}: GetProjectVideosParams): Promise<VideoMetaData[]> => {
  try {
    const { data, error } = await client.query<GetProjectVideosResponse>({
      query: GET_PROJECT_VIDEOS_QUERY,
      variables: { projectSlug, page },
      errorPolicy: 'all',
    })

    if (error) {
      logger().error('Error fetching project videos', {
        error,
        projectSlug,
        page,
      })
    }

    if (!data?.project?.videos) {
      logger().warn('No videos found for project', { projectSlug, page })
      return []
    }

    return data.project.videos
  } catch (error) {
    logger().error('Failed to fetch project videos', {
      error,
      projectSlug,
      page,
    })
    return []
  }
}

export const isNextEpisodeSpecialOrMovie = (
  result: NonNullable<GetNextContentQuery['nextContent']>,
): result is NextContentEpisodeSpecialOrMovie => {
  return (
    result.__typename === 'ContentEpisode' ||
    result.__typename === 'ContentSpecial' ||
    result.__typename === 'ContentMovie'
  )
}

export const useContentAvailability = (content: Maybe<ContentAvailabilityFragment>): UseContentAvailabilityResponse => {
  return useMemo(() => {
    return getContentAvailability(content)
  }, [content])
}

export const getContentAvailability = (content: Maybe<ContentAvailabilityFragment>): UseContentAvailabilityResponse => {
  if (!content) {
    return {
      canWatchNow: false,
      canWatchNowIfGuildUpsell: false,
      watchableAt: null,
      canGuildUpsell: false,
      guildWatchableAt: null,
    }
  }
  const { watchableAt, watchableAvailabilityStatus, actionsToWatch } = content
  const canWatchNow = watchableAvailabilityStatus === 'WATCHABLE'

  const initWatchInfo: Pick<
    UseContentAvailabilityResponse,
    'canGuildUpsell' | 'canWatchNowIfGuildUpsell' | 'guildWatchableAt'
  > = {
    canGuildUpsell: false,
    canWatchNowIfGuildUpsell: false,
    guildWatchableAt: null,
  }
  const { canGuildUpsell, canWatchNowIfGuildUpsell, guildWatchableAt } =
    actionsToWatch?.reduce((acc, action) => {
      if (canWatchNow) {
        return acc
      }
      if (action?.__typename === 'GuildAvailabilityAction') {
        acc.canGuildUpsell = true
        acc.guildWatchableAt = action.startDatetime
        if (acc.guildWatchableAt && isAfter(new Date(), parseISO(acc.guildWatchableAt))) {
          acc.canWatchNowIfGuildUpsell = true
        }
      }
      return acc
    }, initWatchInfo) ?? initWatchInfo

  return {
    canWatchNow,
    canWatchNowIfGuildUpsell,
    watchableAt,
    canGuildUpsell,
    guildWatchableAt,
  }
}

export const useGetNextContent = ({ contentGuid }: { contentGuid?: string }) => {
  const { data, loading } = useQuery<GetNextContentQuery, GetNextContentQueryVariables>(GET_NEXT_CONTENT, {
    skip: !contentGuid,
    variables: {
      contentGuid: contentGuid || '',
    },
    errorPolicy: 'all',
  })

  const currentVideoType = data?.potentialTrailerOrSneakPeekMovie?.__typename

  const nextContent = useMemo(() => {
    if (data?.nextContent && isNextEpisodeSpecialOrMovie(data.nextContent)) {
      const { canWatchNow, canWatchNowIfGuildUpsell } = getContentAvailability(data.nextContent)

      if (canWatchNow || canWatchNowIfGuildUpsell) {
        return {
          ...data.nextContent,
          id: data.nextContent.id,
          image: data.nextContent.image?.cloudinaryPath ?? data.nextContent.posterLandscapeCloudinaryPath ?? '',
        }
      }
    }

    if (currentVideoType === 'ContentTrailer' || currentVideoType === 'ContentSneakPeek') {
      const title = data?.potentialTrailerOrSneakPeekMovie?.project.title

      if (title?.__typename === 'ContentMovie') {
        const { canWatchNow, canWatchNowIfGuildUpsell } = getContentAvailability(title)

        if (canWatchNow || canWatchNowIfGuildUpsell) {
          return {
            ...title,
            id: title.id,
            image: title.image?.cloudinaryPath ?? title.posterLandscapeCloudinaryPath ?? '',
          }
        }
      }
    }
  }, [currentVideoType, data?.nextContent, data?.potentialTrailerOrSneakPeekMovie])

  return {
    nextContent,
    loading,
    currentVideoType,
  }
}

export const useGetTheatricalReleaseBySlug = ({
  theatricalSlug,
  region,
  projectType,
  skip = false,
}: {
  theatricalSlug: Maybe<string>
  region: string
  projectType: Maybe<string>
  skip?: boolean
}) => {
  const isMovie = projectType === projectTypes.movie

  const shouldSkip = skip || !isMovie || !theatricalSlug || !region

  const { data, loading, error } = useQuery<GetTheatricalReleaseBySlugQuery>(GET_THEATRICAL_RELEASE_BY_SLUG, {
    variables: { slug: theatricalSlug, region },
    skip: shouldSkip,
    errorPolicy: 'all',
  })

  const release = data?.theatricalRelease?.theatricalRelease ?? null

  // Filter out invalid/placeholder theatrical releases with all-zeros UUID for licensed titles
  const isValidRelease = release && release.id !== '00000000-0000-0000-0000-000000000000'

  return {
    theatricalRelease: isValidRelease ? release : null,
    loading,
    error,
  }
}

function getCatalogApiDescription(title: unknown): string {
  if (!title || typeof title !== 'object' || !('description' in title)) {
    return ''
  }

  const description = title.description
  if (!description || typeof description !== 'object') {
    return ''
  }

  if ('long' in description && description.long) {
    return description.long as string
  }
  if ('medium' in description && description.medium) {
    return description.medium as string
  }
  if ('short' in description && description.short) {
    return description.short as string
  }

  return ''
}

function getLegacyDescription(title: unknown): string {
  if (!title || typeof title !== 'object') {
    return ''
  }

  if ('mediumDescription' in title && title.mediumDescription) {
    return String(title.mediumDescription)
  }
  if ('shortDescription' in title && title.shortDescription) {
    return String(title.shortDescription)
  }
  if ('longDescription' in title && title.longDescription) {
    return String(title.longDescription)
  }

  return ''
}

function getShowOverview(project: unknown): string {
  if (!project || typeof project !== 'object') {
    return ''
  }

  if ('showOverview' in project && project.showOverview) {
    return project.showOverview as string
  }

  return ''
}

export function getProjectDescription(project: Maybe<Project | ProjectMeta>): string {
  if (!project) {
    return ''
  }

  const catalogDescription = getCatalogApiDescription(project.title)
  if (catalogDescription) {
    return catalogDescription
  }

  const legacyDescription = getLegacyDescription(project.title)
  if (legacyDescription) {
    return legacyDescription
  }

  return getShowOverview(project)
}

export async function getResumeWatchingForProject(
  userId: string | null,
  projectSlug: string,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<ResumeWatchingData | null> {
  if (!userId) {
    return null
  }

  try {
    const result = await client.query({
      query: GET_RESUME_WATCHING_FOR_PROJECT,
      variables: { projectSlug },
      context: {
        requireAuth: true,
      },
      errorPolicy: 'all',
    })

    if (!result) {
      logger().warn('getResumeWatchingForProject query returned undefined', { projectSlug })
      return null
    }

    const edge = result.data?.resumeWatching?.edges?.[0]
    if (!edge?.node) {
      return null
    }

    return edge.node
  } catch (error) {
    logger().error('getResumeWatchingForProject query failed', { projectSlug }, error)
    return null
  }
}
